import React, { useState, useEffect } from 'react';
import ApiClient from '../../utils/ApiClient';

function CompetitorProductsReport() {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [sellers, setSellers] = useState([]);
    const [selectedSellers, setSelectedSellers] = useState(new Set());
    const [searchTerm, setSearchTerm] = useState('');
    const [results, setResults] = useState([]);
    const [hasSearched, setHasSearched] = useState(false);
    const [sortColumn, setSortColumn] = useState('competitorCount');
    const [sortDirection, setSortDirection] = useState('desc');

    useEffect(() => {
        fetchSellers();
    }, []);

    const fetchSellers = async () => {
        try {
            setLoading(true);
            const response = await ApiClient.post('/api/competitor-products-sellers', {});
            setSellers(response.data || []);
        } catch (err) {
            setError('Failed to load sellers');
            console.error('Error fetching sellers:', err);
        } finally {
            setLoading(false);
        }
    };

    const fetchReport = async () => {
        if (selectedSellers.size === 0) {
            setError('Please select at least one seller');
            return;
        }

        try {
            setLoading(true);
            setError(null);
            const response = await ApiClient.post('/api/competitor-products-report', {
                sellerNames: Array.from(selectedSellers)
            });
            setResults(response.data || []);
            setHasSearched(true);
        } catch (err) {
            setError('Failed to load report data');
            console.error('Error fetching report:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleSellerToggle = (sellerName) => {
        setSelectedSellers(prev => {
            const newSet = new Set(prev);
            if (newSet.has(sellerName)) {
                newSet.delete(sellerName);
            } else {
                newSet.add(sellerName);
            }
            return newSet;
        });
    };

    const handleSelectAll = () => {
        const filteredSellerNames = filteredSellers.map(s => s.sellerName);
        const allSelected = filteredSellerNames.every(name => selectedSellers.has(name));

        if (allSelected) {
            setSelectedSellers(new Set());
        } else {
            setSelectedSellers(new Set(filteredSellerNames));
        }
    };

    const formatPrice = (price) => {
        if (price === null || price === undefined) return '-';
        return `$${parseFloat(price).toFixed(2)}`;
    };

    const formatSalesRank = (rank) => {
        if (rank === null || rank === undefined) return '-';
        return rank.toLocaleString();
    };

    const filteredSellers = sellers.filter(s =>
        s.sellerName.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const handleSort = (column) => {
        if (sortColumn === column) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortColumn(column);
            setSortDirection('desc');
        }
    };

    const sortedResults = [...results].sort((a, b) => {
        let aVal = a[sortColumn];
        let bVal = b[sortColumn];

        // Handle null/undefined values - push to end
        if (aVal === null || aVal === undefined) return 1;
        if (bVal === null || bVal === undefined) return -1;

        // String comparison for text fields
        if (sortColumn === 'asin' || sortColumn === 'productName' || sortColumn === 'competitors') {
            aVal = (aVal || '').toLowerCase();
            bVal = (bVal || '').toLowerCase();
            if (sortDirection === 'asc') {
                return aVal.localeCompare(bVal);
            }
            return bVal.localeCompare(aVal);
        }

        // Numeric comparison
        if (sortDirection === 'asc') {
            return aVal - bVal;
        }
        return bVal - aVal;
    });

    const SortIcon = ({ column }) => {
        if (sortColumn !== column) {
            return <span className="ml-1 text-gray-300">&#8597;</span>;
        }
        return sortDirection === 'asc'
            ? <span className="ml-1">&#8593;</span>
            : <span className="ml-1">&#8595;</span>;
    };

    const exportToCsv = () => {
        if (sortedResults.length === 0) return;

        const headers = ['ASIN', 'Amazon Link', 'Product Name', 'Sales Rank', 'Offer Price', 'Competitor Count', 'Competitors'];
        const rows = sortedResults.map(r => [
            r.asin,
            `https://amazon.com/dp/${r.asin}`,
            `"${(r.productName || '').replace(/"/g, '""')}"`,
            r.salesRank || '',
            r.offerPrice || '',
            r.competitorCount,
            `"${r.competitors}"`
        ]);

        const csvContent = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `competitor_products_${new Date().toISOString().split('T')[0]}.csv`;
        link.click();
    };

    if (error && !sellers.length) {
        return (
            <div className="text-center py-12">
                <div className="text-red-600 mb-4">{error}</div>
                <button
                    onClick={() => window.location.reload()}
                    className="text-blue-600 hover:text-blue-800"
                >
                    Try Again
                </button>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="bg-white p-6 rounded-lg shadow">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">
                    Products Competitors Sell That We Don't
                </h2>
                <p className="text-gray-600 mb-6">
                    Select competitors to see which ASINs they're selling that Occam's Razor is not currently selling.
                </p>

                {/* Seller Selection */}
                <div className="mb-6">
                    <div className="flex justify-between items-center mb-4">
                        <h3 className="text-lg font-medium text-gray-900">Select Competitors</h3>
                        <button
                            onClick={handleSelectAll}
                            className="text-blue-600 hover:text-blue-800 text-sm"
                        >
                            {filteredSellers.length > 0 && filteredSellers.every(s => selectedSellers.has(s.sellerName))
                                ? 'Deselect All'
                                : 'Select All'}
                        </button>
                    </div>

                    <div className="mb-4">
                        <input
                            type="text"
                            placeholder="Search sellers..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="block w-full pl-3 pr-3 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md border"
                        />
                    </div>

                    <div className="text-sm text-gray-600 mb-2">
                        {selectedSellers.size} of {filteredSellers.length} sellers selected
                    </div>

                    <div className="max-h-60 overflow-y-auto border border-gray-200 rounded-md p-3 space-y-2">
                        {filteredSellers.map(seller => (
                            <label key={seller.sellerName} className="flex items-center justify-between">
                                <div className="flex items-center">
                                    <input
                                        type="checkbox"
                                        checked={selectedSellers.has(seller.sellerName)}
                                        onChange={() => handleSellerToggle(seller.sellerName)}
                                        className="mr-2 h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                                    />
                                    <span className="text-sm text-gray-700">{seller.sellerName}</span>
                                </div>
                                <span className="text-xs text-gray-500">{seller.asinCount} ASINs</span>
                            </label>
                        ))}
                    </div>
                </div>

                {/* Run Report Button */}
                <div className="flex gap-4">
                    <button
                        onClick={fetchReport}
                        disabled={loading || selectedSellers.size === 0}
                        className={`px-6 py-2 text-white text-sm font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 ${
                            loading || selectedSellers.size === 0
                                ? 'bg-gray-400 cursor-not-allowed'
                                : 'bg-indigo-600 hover:bg-indigo-700'
                        }`}
                    >
                        {loading ? 'Loading...' : 'Run Report'}
                    </button>

                    {results.length > 0 && (
                        <button
                            onClick={exportToCsv}
                            className="px-6 py-2 bg-green-600 text-white text-sm font-medium rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                        >
                            Export CSV
                        </button>
                    )}
                </div>

                {error && (
                    <div className="mt-4 text-red-600 text-sm">{error}</div>
                )}
            </div>

            {/* Results Table */}
            {hasSearched && (
                <div className="bg-white p-6 rounded-lg shadow">
                    <div className="flex justify-between items-center mb-4">
                        <h3 className="text-lg font-medium text-gray-900">
                            Results ({results.length} products)
                        </h3>
                    </div>

                    {results.length === 0 ? (
                        <div className="text-center py-12 text-gray-600">
                            No products found. The selected competitors are not selling any products that Occam's Razor isn't already selling.
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th
                                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                                            onClick={() => handleSort('asin')}
                                        >
                                            ASIN<SortIcon column="asin" />
                                        </th>
                                        <th
                                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                                            onClick={() => handleSort('productName')}
                                        >
                                            Product Name<SortIcon column="productName" />
                                        </th>
                                        <th
                                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                                            onClick={() => handleSort('salesRank')}
                                        >
                                            Sales Rank<SortIcon column="salesRank" />
                                        </th>
                                        <th
                                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                                            onClick={() => handleSort('offerPrice')}
                                        >
                                            Offer Price<SortIcon column="offerPrice" />
                                        </th>
                                        <th
                                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                                            onClick={() => handleSort('competitorCount')}
                                        >
                                            # Competitors<SortIcon column="competitorCount" />
                                        </th>
                                        <th
                                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                                            onClick={() => handleSort('competitors')}
                                        >
                                            Competitors Selling<SortIcon column="competitors" />
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {sortedResults.map((item, index) => (
                                        <tr key={item.asin} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm">
                                                <a
                                                    href={`https://amazon.com/dp/${item.asin}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="text-blue-600 hover:text-blue-800 hover:underline"
                                                >
                                                    {item.asin}
                                                </a>
                                            </td>
                                            <td className="px-6 py-4 text-sm text-gray-900 max-w-xs truncate" title={item.productName || ''}>
                                                {item.productName || <span className="text-gray-400">-</span>}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                {formatSalesRank(item.salesRank)}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                {formatPrice(item.offerPrice)}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                                    item.competitorCount >= 3
                                                        ? 'bg-red-100 text-red-800'
                                                        : item.competitorCount === 2
                                                            ? 'bg-yellow-100 text-yellow-800'
                                                            : 'bg-gray-100 text-gray-800'
                                                }`}>
                                                    {item.competitorCount}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 text-sm text-gray-500">
                                                {item.competitors}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

export default CompetitorProductsReport;
